<script setup>
/**
 * Byggeren paa en kategoriside, med faerdige maskiner naar der er nogen.
 *
 * Staar KUN paa de tolv kategorier byggeren henter dele fra. Serveren sender
 * null paa alle andre, og sektionen renderes slet ikke — se Byggeforslag.php.
 *
 * KORTENE ER EN BONUS, IKKE EN BETINGELSE. Ni af de tolv deltyper har et
 * brugbart offentligt build; skaerm, tastatur og mus har nul. pc-skaerm er den
 * stoerste af de tolv med 3.312 varer og 623 eksponeringer paa seks uger, saa
 * en regel der kraevede kort ville have lukket den side der har mest at vinde.
 *
 * Buildkortene er de samme som paa spilsiderne: hvert kort linker til sin egen
 * build-side OG til hver enkelt del, saa kategorisiden faar udgaaende links til
 * baade maskiner og varer.
 *
 * Navnene kommer boejet fra serveren. "omkring " + titlen + "et" giver
 * "processoret" og "harddisket"; se Delnavne.php.
 */
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';
import Buildkort from '@/Components/PcBygger/Buildkort.vue';

const props = defineProps({
    forslag: { type: Object, required: true },
});

const antal = computed(() =>
    new Intl.NumberFormat('da-DK').format(props.forslag.antal_i_byggeren || 0)
);

/*
 * En skaerm er ikke noget man bygger en maskine OMKRING, og den har hverken
 * sokkel eller stroemkrav vi kan regne paa. Begge saetninger skifter derfor
 * med gruppen. Se Byggegrupper.
 */
const overskrift = computed(() =>
    props.forslag.er_maskinen
        ? `Byg en hel PC omkring ${props.forslag.navne.bestemt}`
        : `Tag ${props.forslag.navne.bestemt} med i dit PC-build`
);
</script>

<template>
    <section class="card rounded-0 mb-4 shadow-sm">
        <div class="card-body py-4 px-4 text-start">
            <h2 class="h5 mb-2">{{ overskrift }}</h2>

            <p class="bf-intro mb-4">
                <!--
                    Tallet staar der for at vise at listen ikke er et udvalg vi
                    har sat sammen i haanden. Og kompatibiliteten naevnes fordi
                    det er det eneste byggeren kan som en prisliste ikke kan.
                -->
                <Link href="/pc-bygger">PC-byggeren</Link> har {{ antal }}
                {{ forslag.navne.flertal }} at vælge imellem

                <template v-if="forslag.er_maskinen">
                    og holder øje med at delene passer: sokkel, hukommelsestype,
                    plads i kabinettet og strøm.
                </template>
                <template v-else>
                    og lægger dem oven i maskinen, så prisen på hele opsætningen
                    står ét sted.
                </template>

                <template v-if="forslag.builds.length">
                    Her er færdige maskiner til dagens priser.
                </template>
            </p>

            <div v-if="forslag.builds.length" class="bf-gitter">
                <Buildkort v-for="b in forslag.builds" :key="b.slug" :kort="b" />
            </div>

            <p class="bf-bund mb-0" :class="forslag.builds.length ? 'mt-3' : ''">
                <Link href="/pc-bygger/ny">Sæt din egen sammen</Link>
                &middot;
                <Link href="/pc-bygger/builds">Se alle offentlige builds</Link>
            </p>
        </div>
    </section>
</template>

<style scoped>
.bf-intro {
    color: var(--pc-text-secondary);
    max-width: 62ch;
}

.bf-gitter {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
}

.bf-bund {
    font-size: .88rem;
    color: var(--pc-text-secondary);
}
</style>
